import React from "react";
import { ExternalLink, Users, Target, Heart, Star, CheckCircle, Baby } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";

const FootballXJoeys = () => {
  const handleEOI = async () => {
    try {
      toast.success("Redirecting to Expression of Interest form...");
      window.open("https://forms.gle/nLZ5qvGLD67VvWnGA", '_blank');
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const programFeatures = [
    {
      title: "Fun-Focused Learning",
      description: "Beginner-friendly program that makes learning football enjoyable and engaging for young children.",
      icon: <Heart className="w-8 h-8 text-blue-500" />
    },
    {
      title: "Basic Skill Development",
      description: "Focus on developing fundamental football skills and coordination through play-based activities.",
      icon: <Target className="w-8 h-8 text-blue-500" />
    },
    {
      title: "Safe Environment",
      description: "Supportive and safe training environment designed specifically for young children.",
      icon: <Users className="w-8 h-8 text-blue-500" />
    },
    {
      title: "Love for the Game",
      description: "Building a foundation of love and passion for football from an early age.",
      icon: <Star className="w-8 h-8 text-blue-500" />
    }
  ];

  const benefits = [
    "Age-appropriate training for 3-7 year olds",
    "Development of basic football skills and coordination", 
    "Fun and engaging play-based learning approach",
    "Safe and supportive environment for beginners",
    "Building confidence and social skills",
    "Fostering a lifelong love for football"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-black text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                FOOTBALL X
              </h1>
              <h2 className="text-2xl md:text-4xl font-bold mb-8 text-blue-400">
                JOEYS PROGRAM
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
                Fun, beginner-friendly program for boys and girls aged 3 to 7 years. 
                Developing basic football skills, coordination, and a love for the game!
              </p>
              <Button 
                onClick={handleEOI}
                size="lg" 
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold"
              >
                Expression of Interest <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/wnd49xet_IMG_1487%20%281%29.JPG"
                alt="Football X Joeys Program Logo"
                className="w-full max-w-md mx-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Program Information */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-black mb-6">
                EXPRESSION OF INTEREST
              </h2>
              <h3 className="text-2xl font-semibold text-blue-600 mb-8">
                Joeys Program (Ages 3-7)
              </h3>
            </div>

            <Card className="mb-12 border-2 border-blue-200">
              <CardContent className="p-8">
                <div className="flex items-center justify-center mb-6">
                  <Baby className="w-12 h-12 text-blue-500 mr-4" />
                  <h3 className="text-2xl font-bold text-blue-600">Ages 3-7 Years</h3>
                </div>
                
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Football X FC is excited to introduce the Joeys Program, designed for boys and girls aged 3 to 7 years. 
                  This fun, beginner-friendly program focuses on developing basic football skills, coordination, and a love 
                  for the game in a safe and supportive environment.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  We invite parents and guardians to register their Expression of Interest to secure a place for their 
                  child in our inaugural Joeys Program. Spots are limited, so register early to be part of this exciting 
                  football journey!
                </p>

                <div className="text-center">
                  <Button 
                    onClick={handleEOI}
                    size="lg" 
                    className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3"
                  >
                    Submit Expression of Interest <ExternalLink className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Program Features */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              PROGRAM <span className="text-blue-500">FEATURES</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our Joeys Program is specially designed to introduce young children to football 
              in a fun, safe, and age-appropriate way.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {programFeatures.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 hover:border-blue-500">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-black mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="max-w-3xl mx-auto">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-black mb-6 text-center">What Your Child Will Learn</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Age Group Highlight */}
      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="flex items-center justify-center mb-6">
              <Baby className="w-16 h-16 mr-4" />
              <h2 className="text-5xl font-bold">3-7 YEARS</h2>
            </div>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              The perfect age to start their football journey! Our Joeys Program introduces young players 
              to the beautiful game through fun, play-based activities designed just for them.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">Play-Based</h3>
                <p className="text-blue-100">Learning through fun activities and games</p>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">Age-Appropriate</h3>
                <p className="text-blue-100">Designed specifically for 3-7 year olds</p>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">Limited Spots</h3>
                <p className="text-blue-100">Register early to secure your child's place</p>
              </div>
            </div>
            <Button 
              onClick={handleEOI}
              size="lg" 
              className="bg-white hover:bg-gray-100 text-blue-600 font-semibold px-8 py-3"
            >
              Register Your Child Today <ExternalLink className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FootballXJoeys;